import React, { createContext, useContext, useMemo } from 'react';
import { useAuth } from './AuthContext';

const PermissionContext = createContext();

export const usePermissions = () => {
  const context = useContext(PermissionContext);
  if (!context) {
    throw new Error('usePermissions must be used within a PermissionProvider');
  }
  return context;
};

export const PermissionProvider = ({ children }) => {
  const { user } = useAuth();

  // Get user permissions from the user object
  const userPermissions = useMemo(() => {
    if (!user?.permissions) return [];
    return user.permissions.map(permission => permission.slug);
  }, [user?.permissions]);

  // Get user roles from the user object
  const userRoles = useMemo(() => {
    if (!user?.roles) return [];
    return user.roles.map(role => role.slug);
  }, [user?.roles]);

  // Check if user has a specific permission
  const hasPermission = (permission) => {
    if (!userPermissions.length) return false;
    return userPermissions.includes(permission);
  };

  // Check if user has any of the given permissions
  const hasAnyPermission = (permissions) => {
    if (!userPermissions.length || !permissions.length) return false;
    return permissions.some(permission => userPermissions.includes(permission));
  };

  // Check if user has all of the given permissions
  const hasAllPermissions = (permissions) => {
    if (!userPermissions.length || !permissions.length) return false;
    return permissions.every(permission => userPermissions.includes(permission));
  };

  // Check if user has a specific role
  const hasRole = (role) => {
    if (!userRoles.length) return false;
    return userRoles.includes(role);
  };

  // Check if user has any of the given roles
  const hasAnyRole = (roles) => {
    if (!userRoles.length || !roles.length) return false;
    return roles.some(role => userRoles.includes(role));
  };

  // Check if user is admin (super_admin or admin)
  const isAdmin = () => {
    return hasAnyRole(['super_admin', 'admin']);
  };

  // Check if user is super admin
  const isSuperAdmin = () => {
    return hasRole('super_admin');
  };

  // Check if user is manager
  const isManager = () => {
    return hasRole('manager');
  };

  // Check if user is cashier
  const isCashier = () => {
    return hasRole('cashier');
  };

  // Check if user is viewer
  const isViewer = () => {
    return hasRole('viewer');
  };

  const value = {
    userPermissions,
    userRoles,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    hasRole,
    hasAnyRole,
    isAdmin,
    isSuperAdmin,
    isManager,
    isCashier,
    isViewer,
  };

  return (
    <PermissionContext.Provider value={value}>
      {children}
    </PermissionContext.Provider>
  );
};

export default PermissionContext;